import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Button, Flex, Input, Tag, Tooltip, notification } from 'antd';
import { EditOutlined, EyeOutlined, FileDoneOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import './styles.css';
import TableComponent from '../other/TableComponent';
import PoDrawer from './PoDrawer';
import { contracts } from '../api';
import { handlestatus, highlightSearchText } from '../other/usefulFunctions';
import SelectStatus from '../other/SelectStatus';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import Filter from '../other/Filter';
import { userRole, employeeId } from '../../GetRole.utils';

const Po = () => {
  const location = useLocation();
  const { state } = location;
  const [contractData, setContractData] = useState([]);
  const [totalRecords, setTotalRecords] = useState([]);
  const [showBtn, setShowBtn] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [loadingApproval, setLoadingApproval] = useState(null);
  const [filterFields, setFilterFields] = useState({
    page: 1,
    filterField: '',
    sorting: { field: '', order: '' },
    filterValue: '',
  });
  const navigate = useNavigate();

  const [selectedStatus, setSelectedStatus] = useState(null);
  const [breadcrumbActionsEl, setBreadcrumbActionsEl] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [globalSearch, setGlobalSearch] = useState('');

  
  useEffect(() => {
    const timer = setTimeout(() => setGlobalSearch(searchInput.trim()), 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setBreadcrumbActionsEl(document.getElementById('breadcrumb-actions'));
  }, []);

  const getInitialStatus = () => {
      const statusParam = searchParams.get('status');
      return statusParam ? statusParam.split(',').map(Number) : [1, 2, 3];
    };
  
    const getInitialExpiredDays = () => {
      return searchParams.get('expiredDays') || '';
    };
  
    const [contractFilters, setContractFilters] = useState({
      status: getInitialStatus(),
      expiredDays: getInitialExpiredDays(),
    });

  const openNotificationWithIcon = (type, message) => {
    notification[type]({
      message: message,
      style: {
        padding: '9px 24px',
      },
    });
  };

  const showModal = (key, status) => {
    setSelectedKey(key);
    setSelectedStatus(status || null);
    setIsModalOpen(true);
  };

  const handlePopupCancel = () => {
    setSelectedKey(null);
    setIsModalOpen(false);
  };

  const handleFilters = (value) => {
    setFilterFields((prevState) => {
      if (
        prevState.page !== value.page ||
        prevState.filterField !== value.filterField ||
        prevState.sorting.field !== value.sorting.field ||
        prevState.sorting.order !== value.sorting.order ||
        prevState.filterValue !== value.filterValue
      ) {
        return {
          page: value.page,
          filterField: value.filterField,
          sorting: {
            field: value.sorting.field,
            order: value.sorting.order,
          },
          filterValue: value.filterValue,
        };
      } else {
        return prevState;
      }
    });
  };

  const handleContractStatus = (values) => {
    setContractFilters((prev) => {
      const newFilters = { ...prev, status: values };
      setSearchParams((params) => {
        params.set('status', values.join(','));
        params.set('expiredDays', newFilters.expiredDays);
        return params;
      });
      return newFilters;
    });
  };

  const handleExpireDays = (value) => {
    setContractFilters((prev) => {
      const newFilters = { ...prev, expiredDays: value };
      setSearchParams((params) => {
        params.set('status', newFilters.status.join(','));
        params.set('expiredDays', value);
        return params;
      });
      return newFilters;
    });
  };

  const handleApproval = (id) => {
    const postData = {
      cStatusId: 2,
      createdBy: employeeId,
    };
    setLoadingApproval(id);
    contracts
      .updateContractsData(id, postData)
      .then((res) => {
        openNotificationWithIcon('success', 'Contract sent for Approval');
        setShowBtn((prev) => !prev);
        setLoadingApproval(null);
      })
      .catch((err) => {
        console.log(err);
        openNotificationWithIcon('error', err.response.data.message);
        setLoadingApproval(null);
      });
  };

  const columns = [
    {
      title: 'Legal Entity Name',
      dataIndex: 'legalEntityName',
      key: 'legalEntityName',
      width: '15%',
    },
    {
      title: 'PO Number',
      dataIndex: 'poNumber',
      key: 'poNumber',
      width: '15%',
    },
    {
      title: 'End Date',
      dataIndex: 'expiryDate',
      width: '15%',
      key: 'expiryDate',
    },
    {
      title: 'Status',
      dataIndex: 'cStatus',
      key: 'cStatus',
      width: '15%',
      render: (_, record) => handlestatus(record.cStatus),
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      width: '15%',
      fixed: 'right',
      render: (_, record) => (
        <div
          style={{ display: 'flex', gap: '8px', justifyContent: 'flex-start' }}
        >
          <div style={{ width: '32px', textAlign: 'center' }}>
            {(record.cStatus === 'draft' ||
              record.cStatus === 'pendingapproval' ||
              record.cStatus === 'inactive') &&
            userRole !== 'finance' ? (
              <Tooltip title="Edit" key={'Edit'}>
                <Button
                  onClick={() => showModal(record.contractId, record.cStatus)}
                  type="link"
                >
                  <EditOutlined />
                </Button>{' '}
              </Tooltip>
            ) : null}
          </div>

          <div style={{ width: '32px', textAlign: 'center' }}>
            {
              <Tooltip title="View" key={'View'}>
                <Button
                  type="link"
                  style={{
                    color: '#fa541c',
                  }}
                  onClick={() => navigate(`/contract/po/${record?.contractId}`)}
                >
                  <EyeOutlined />
                </Button>
              </Tooltip>
            }
          </div>
          
          <div style={{ width: '32px', textAlign: 'center' }}>
            {record.cStatus === 'draft' && userRole !== 'finance' ? (
              <Tooltip title="Send for Approval" key={'Approval'}>
                <Button
                  type="link"
                  style={{
                    color: '#52c41a',
                  }}
                  onClick={() => handleApproval(record?.contractId)}
                  loading={loadingApproval === record?.contractId}
                >
                  <FileDoneOutlined />
                </Button>
              </Tooltip>
            ) : null}
          </div>
        </div>
      ),
    },
  ];

  useEffect(() => {
    fetchData();
  }, [isModalOpen, showBtn, filterFields, contractFilters]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await contracts.contractsAllData(
        filterFields.page,
        'po',
        contractFilters.status,
        filterFields.sorting.field,
        filterFields.sorting.order,
        filterFields.filterField,
        filterFields.filterValue,
        contractFilters.expiredDays
      );
      const data = response.data;

      setContractData(data.data);
      setTotalRecords(data.totalContractCount);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <h2 style={{ margin: 0 }}>Purchase order (PO)</h2>

      <PoDrawer
        open={isModalOpen}
        contractId={selectedKey}
        status={selectedStatus}
        onClose={handlePopupCancel}
      />
      {breadcrumbActionsEl &&
        ReactDOM.createPortal(
          <Flex align="center" gap={12} wrap="wrap">
            <Tooltip title="Search by Client, Project, PO Number, Date">
              <Input
                allowClear
                prefix={<SearchOutlined style={{ color: '#98A2B3' }} />}
                placeholder="Search by Client, Project, PO Number, Date"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                style={{ width: 280 }}
              />
            </Tooltip>
            <div style={{ position: 'relative' }}>
              <Filter
                handleContractStatus={handleContractStatus}
                handleExpireDays={handleExpireDays}
                expiredDays={contractFilters.expiredDays}
                status={contractFilters.status}
              />
            </div>
            {userRole !== 'finance' && (
              <Button onClick={() => showModal(null)} type="primary" icon={<PlusOutlined />}>
                New PO
              </Button>
            )}
          </Flex>,
          breadcrumbActionsEl
        )}
      <TableComponent
        loading={loading}
        data={contractData}
        columns={columns}
        scrollX={1200}
        totalPages={totalRecords}
        onFilters={handleFilters}
        size="small"
        disableColumnSearch
        searchField="global"
        searchValue={globalSearch}
      />
    </>
  );
};

export default Po;
